import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Loader2, Command, Link2, Copy, Search, Download, Sparkles, X, Send, Bot,
  Settings, Moon, Sun, LogOut, Bell, Menu, HelpCircle, ExternalLink, Receipt, UserPlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getAuthToken, clearAuthTokens } from '../utils/auth';
import { useTheme } from '../context/ThemeContext';

// Dashboard Components
import DashboardSidebar from '../components/dashboard/DashboardSidebar';
import KanbanPipeline from '../components/dashboard/KanbanPipeline';
import CommandBar from '../components/dashboard/CommandBar';
import AIBriefing from '../components/dashboard/AIBriefing';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import QuickStats from '../components/dashboard/QuickStats';
import OnboardingChecklist from '../components/dashboard/OnboardingChecklist';
import GettingStartedModal from '../components/dashboard/GettingStartedModal';
import LinkConversionInsight from '../components/dashboard/LinkConversionInsight';
import GoogleConnectHeader from '../components/dashboard/GoogleConnectHeader';

// Existing Tab Components
import DealerAnalyticsSection from '../components/DealerAnalyticsSection';
import DealerSignupsTab from '../components/DealerSignupsTab';
import EmailCenterTab from '../components/EmailCenterTab';
import SalesTrackerTab from '../components/SalesTrackerTab';
import LeadDetailModal from '../components/LeadDetailModal';
import AppointmentsTab from '../components/dashboard/AppointmentsTab';

// Tools Components (embedded)
import InventoryManagerEmbed from '../components/tools/InventoryManagerEmbed';
import CRMHubEmbed from '../components/tools/CRMHubEmbed';
import WebResearchEmbed from '../components/tools/WebResearchEmbed';
import ResourcesTab from '../components/tools/ResourcesTab';
import SalesWarBoard from '../components/dashboard/SalesWarBoard';
import CustomerIntakeForm from './CustomerIntakeForm';
import TradesTab from '../components/dashboard/TradesTab';
import DealerSupportChat from '../components/dealer/DealerSupportChat';
import MarketingTab from '../components/tools/MarketingTab';
// VIP Tools are now embedded in ResourcesTab

// Admin Components
import AdminPanel from '../components/admin/AdminPanel';
import AddLeadModal from '../components/dashboard/AddLeadModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DealerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Dealer dashboard is always rendered against the cinematic dark palette
  // so it stays consistent with the homepage and customer matches dashboard.
  // We still call useTheme so the light/dark toggle stays functional for the
  // user's preference elsewhere in the app — but the dashboard itself locks
  // to dark for visual consistency.
  const { toggleTheme } = useTheme();
  const isDark = true;
  
  // Core State
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dealer, setDealer] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  
  // UI State
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCommandBar, setShowCommandBar] = useState(false);
  // Getting Started guide modal — opened via the "?" icon in the top nav
  // OR the "Open guide" button on the onboarding checklist.
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);

  // ---------- Onboarding action helpers (shared across sub-panels) ----------
  const copyMyQuizLink = async () => {
    const slug = (dealer?.slug || '').trim();
    const url = slug
      ? `${window.location.origin}/${slug}`
      : `${window.location.origin}/quiz`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Copied ${url}`);
      // Record activation event (fire-and-forget)
      const t = getAuthToken();
      if (t) {
        axios.post(`${API}/dealer/activation/event`, { event: 'quiz_link_copied' },
          { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
      }
    } catch {
      toast.error('Copy failed — grab it from Marketing → Sharing.');
    }
  };

  const openMyPublicPage = () => {
    const slug = (dealer?.slug || '').trim();
    if (!slug) {
      toast.error('Set your slug in Marketing → Sharing first.');
      setSearchParams({ tab: 'marketing' });
      return;
    }
    window.open(`${window.location.origin}/${slug}`, '_blank', 'noopener');
    const t = getAuthToken();
    if (t) {
      axios.post(`${API}/dealer/activation/event`, { event: 'public_page_viewed' },
        { headers: { Authorization: `Bearer ${t}` } }).catch(() => {});
    }
  };

  const createTestLead = async () => {
    const t = getAuthToken();
    if (!t) return;
    try {
      const res = await axios.post(`${API}/dealer/test-lead`, {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.data?.success) {
        toast.success('Test lead created — check your Pipeline.');
        // Refresh leads so the checklist auto-checks + it appears in Kanban
        const leadsRes = await axios.get(`${API}/dealer/leads`,
          { headers: { Authorization: `Bearer ${t}` } });
        setLeads(leadsRes.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create test lead.');
    }
  };
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [unreadEmailCount, setUnreadEmailCount] = useState(0);
  const [pendingCallCount, setPendingCallCount] = useState(0);
  const [newLeadCount, setNewLeadCount] = useState(0);
  const [openDealCount, setOpenDealCount] = useState(0);
  const [unreadSupportCount, setUnreadSupportCount] = useState(0);
  
  // AI Chat State
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m your AI Form GPT. I can help you customize your form questions, adjust the AI responses, and tailor the experience for your customers. What would you like to do?' }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Sales Conversion State
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);

  // Get current tab from URL
  const currentTab = searchParams.get('tab') || 'overview';

  // When the dealer visits the Pipeline tab, mark every new lead as viewed
  // so the red badge naturally clears once they've seen the list.
  useEffect(() => {
    if (currentTab !== 'pipeline') return;
    const clear = async () => {
      try {
        const token = getAuthToken();
        await axios.post(`${API}/dealer/leads/mark-all-viewed`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Re-fetch notifications right after clearing so the badge disappears
        // immediately (rather than waiting for the 60s poll interval).
        try {
          const res = await axios.get(`${API}/dealer/notifications`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const n = res.data || {};
          setUnreadEmailCount(n.unread_emails || 0);
          setPendingCallCount(n.pending_calls || 0);
          setNewLeadCount(n.new_leads || 0);
          setOpenDealCount(n.open_deals || 0);
        } catch (_) {}
      } catch (_) {}
    };
    clear();
  }, [currentTab]);

  // Poll support-chat unread badge every 15s so the "Support" nav item
  // lights up when the super admin replies. Cheap query, safe to poll.
  useEffect(() => {
    if (!dealer?.id) return;
    let cancelled = false;
    const pull = async () => {
      try {
        const token = getAuthToken();
        const r = await axios.get(`${API}/support/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUnreadSupportCount(r.data?.unread || 0);
      } catch { /* silent */ }
    };
    pull();
    const iv = setInterval(pull, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [dealer?.id]);


  // Persist last active tab per dealer (localStorage) — restores on login/navigation
  const LAST_TAB_KEY = dealer?.email ? `dealer_last_tab_${dealer.email}` : null;

  // Save current tab whenever it changes
  useEffect(() => {
    if (LAST_TAB_KEY && searchParams.get('tab')) {
      localStorage.setItem(LAST_TAB_KEY, currentTab);
    }
  }, [currentTab, LAST_TAB_KEY, searchParams]);

  // On initial dealer load, if no tab in URL, restore last saved tab
  useEffect(() => {
    if (dealer?.email && !searchParams.get('tab')) {
      const savedTab = localStorage.getItem(`dealer_last_tab_${dealer.email}`);
      if (savedTab && savedTab !== 'overview') {
        setSearchParams({ tab: savedTab }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealer?.email]);

  // Google OAuth landing — /dealer?google=connected|error&msg=… . The
  // toaster isn't reliably mounted this early (toast was silently lost),
  // so stash the result and let GoogleConnectHeader surface it once the
  // dashboard header is actually rendered.
  useEffect(() => {
    const gs = searchParams.get('google');
    if (!gs) return;
    sessionStorage.setItem(
      'google_oauth_result',
      JSON.stringify({ status: gs, msg: searchParams.get('msg') || '' }),
    );
    const next = new URLSearchParams(searchParams);
    next.delete('google');
    next.delete('msg');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Keyboard shortcut for Command Bar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandBar(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Listen for openLeadModal event from CommandBar
  useEffect(() => {
    const handleOpenLeadModal = (e) => {
      const leadId = e.detail?.leadId;
      if (leadId) {
        const lead = leads.find(l => l.id === leadId);
        if (lead) {
          setSelectedLead(lead);
          setShowLeadModal(true);
        }
      }
    };
    window.addEventListener('openLeadModal', handleOpenLeadModal);
    return () => window.removeEventListener('openLeadModal', handleOpenLeadModal);
  }, [leads]);

  // Tracks previous email/lead counts so we only toast on *new* arrivals,
  // not on every poll tick. Ref (not state) so it doesn't re-render.
  const prevCountsRef = useRef({ unread_emails: null, new_leads: null });

  useEffect(() => {
    checkAuthAndFetchLeads();
    fetchNotifications();
    // Ask once for OS-level notification permission so background toasts work
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (_) { /* ignore */ }
    }
    // Poll every 30s so buyer replies surface fast without hammering the API
    const t = setInterval(fetchNotifications, 30000);
    return () => clearInterval(t);
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const n = res.data || {};
      const newUnread = n.unread_emails || 0;
      const newLeads = n.new_leads || 0;
      const prev = prevCountsRef.current;

      // Only fire toast when we have a previous baseline and a genuine increase
      if (prev.unread_emails !== null && newUnread > prev.unread_emails) {
        const delta = newUnread - prev.unread_emails;
        toast.success(
          delta === 1
            ? 'New buyer reply just landed in your Email Center'
            : `${delta} new buyer replies just landed in your Email Center`,
          {
            duration: 8000,
            action: {
              label: 'Open Inbox',
              onClick: () => navigate('/dealer?tab=email'),
            },
          },
        );
        // Best-effort native browser notification for background tabs
        if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification('RV Match A.I. — New Reply', {
              body: `${delta} new buyer ${delta === 1 ? 'reply is' : 'replies are'} waiting in your Email Center.`,
              icon: '/icons/icon-192.png',
              tag: 'rv-match-reply',
            });
          } catch (_) { /* ignore */ }
        }
      }

      if (prev.new_leads !== null && newLeads > prev.new_leads) {
        const delta = newLeads - prev.new_leads;
        toast.info(
          delta === 1 ? 'A fresh lead just hit your Pipeline' : `${delta} fresh leads just hit your Pipeline`,
          {
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => navigate('/dealer?tab=pipeline'),
            },
          },
        );
      }

      prevCountsRef.current = { unread_emails: newUnread, new_leads: newLeads };

      setUnreadEmailCount(newUnread);
      setPendingCallCount(n.pending_calls || 0);
      setNewLeadCount(newLeads);
      setOpenDealCount(n.open_deals || 0);
    } catch (_e) {
      // Silent — non-fatal
    }
  };

  const checkAuthAndFetchLeads = async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const [profileRes, leadsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/dealer/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dealer/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/dealer/analytics`, { headers: { Authorization: `Bearer ${token}` }, params: { days: 30 } }),
      ]);
      
      setDealer(profileRes.data);
      setLeads(leadsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Please log in as a dealer to access this page');
        navigate('/auth');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/inbox/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadEmailCount(res.data.unread_count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const fetchPendingCallCount = async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/dealer/call-bookings/pending-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingCallCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Error fetching pending call count:', err);
    }
  };

  const updateLeadStatus = async (leadId, newStatus, extra = {}) => {
    const token = getAuthToken();
    try {
      // Backend expects body { status } — sending as query string was a no-op
      await axios.patch(
        `${API}/dealer/lead/${leadId}`,
        { status: newStatus, ...extra },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus, ...extra } : lead
      ));
      toast.success('Lead status updated');
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead status');
    }
  };

  const deleteLead = async (leadId) => {
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/dealer/lead/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(leads.filter(lead => lead.id !== leadId));
      setShowLeadModal(false);
      setSelectedLead(null);
      toast.success('Lead deleted. They can now resubmit the form.');
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleLeadClickById = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      handleLeadClick(lead);
    }
  };

  const handleConvertToSale = (lead) => {
    setLeadToConvert(lead);
    setSearchParams({ tab: 'sales' });
    toast.info(`Converting ${lead.name || lead.email} to sale. Fill in the sale details.`);
  };

  const handleLogout = () => {
    clearAuthTokens();
    navigate('/auth');
  };

  // AI Chat handler
  const handleAIChatSubmit = async () => {
    if (!aiInput.trim() || aiLoading) return;
    
    const userMessage = aiInput.trim();
    setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiInput('');
    setAiLoading(true);

    try {
      const token = getAuthToken();
      const response = await axios.post(`${API}/dealer/ai-assistant`, 
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setAiMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an issue. Please try again.' 
      }]);
    } finally {
      setAiLoading(false);
    }
  };

  const exportLeads = async (format) => {
    const token = getAuthToken();
    try {
      const response = await axios.get(`${API}/dealer/export/${format}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (format === 'json') {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rv_leads_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
      } else if (format === 'csv') {
        const dataBlob = new Blob([response.data.csv_data], { type: 'text/csv' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename;
        link.click();
      }
      toast.success(`Leads exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export leads');
    }
  };

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#05070d]' : 'bg-slate-50'}`} data-testid="loading-state">
        <div className="text-center">
          <Loader2 className="animate-spin text-amber-400 mx-auto mb-4" size={48} />
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen relative ${isDark ? 'bg-[#05070d]' : 'bg-slate-50'}`} data-testid="dealer-dashboard">
      {/* Cinematic ambient glow — only in dark mode, matches homepage palette */}
      {isDark && (
        <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-0 overflow-hidden">
          <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-[0.08] blur-[140px]" />
          <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-amber-500 opacity-[0.08] blur-[140px]" />
        </div>
      )}
      {/* Impersonation Banner */}
      {typeof window !== 'undefined' && localStorage.getItem('impersonating') === 'true' && (
        <div className="bg-amber-500 text-black px-4 py-2 flex items-center justify-between z-[100] fixed top-0 left-0 right-0" data-testid="impersonation-banner">
          <div className="flex items-center gap-2">
            <span className="font-semibold">⚠️ Impersonation Mode:</span>
            <span>You are viewing {dealer?.business_name || 'this dealer'}'s dashboard as an admin</span>
          </div>
          <button            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('impersonating');
              window.close();
            }}
            className="bg-black text-white px-4 py-1 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            Exit Impersonation
          </button>
        </div>
      )}

      {/* Sidebar */}
      <DashboardSidebar 
        dealer={dealer}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        isDark={isDark}
        unreadEmailCount={unreadEmailCount}
        pendingCallCount={pendingCallCount}
        newLeadCount={newLeadCount}
        openDealCount={openDealCount}
        unreadSupportCount={unreadSupportCount}
        mobileOpen={mobileMenuOpen}
        onMobileToggle={() => setMobileMenuOpen(false)}
      />

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0 ${typeof window !== 'undefined' && localStorage.getItem('impersonating') === 'true' ? 'pt-10' : ''}`}>
        {/* Top Bar */}
        <header className={`sticky z-30 h-16 flex items-center justify-between px-4 lg:px-6 border-b backdrop-blur-xl ${typeof window !== 'undefined' && localStorage.getItem('impersonating') === 'true' ? 'top-10' : 'top-0'} ${
          isDark ? 'bg-[#05070d]/85 border-white/10' : 'bg-white/80 border-slate-200'
        }`}>
          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className={`lg:hidden p-2 rounded-lg mr-2 ${
              isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
            }`}
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>

          {/* Command Bar Trigger */}
          <button
            onClick={() => setShowCommandBar(true)}
            className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 rounded-xl border transition-all hover:scale-[1.02] flex-1 lg:flex-none ${
              isDark 
                ? 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:border-slate-600' 
                : 'bg-slate-50 border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300'
            }`}
            data-testid="command-bar-trigger"
          >
            <Search size={18} />
            <span className="text-sm hidden sm:inline">Search or jump to...</span>
            <span className="text-sm sm:hidden">Search...</span>
            <kbd className={`hidden sm:flex items-center gap-0.5 px-2 py-0.5 rounded text-xs ${
              isDark ? 'bg-slate-700 text-slate-400' : 'bg-slate-200 text-slate-500'
            }`}>
              <Command size={12} />K
            </kbd>
          </button>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Advisor Link */}
            {dealer && (
              <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${
                isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
              }`}>
                <Link2 size={14} />
                <code className="font-mono">
                  {dealer.email === 'malleg85@gmail.com' 
                    ? window.location.origin 
                    : `${window.location.origin}/${dealer.slug}`}
                </code>
                <button
                  onClick={() => {
                    const link = dealer.email === 'malleg85@gmail.com'
                      ? window.location.origin
                      : `${window.location.origin}/${dealer.slug}`;
                    navigator.clipboard.writeText(link);
                    toast.success('Form link copied!');
                  }}
                  className={`p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
                  data-testid="copy-dealer-link"
                >
                  <Copy size={12} />
                </button>
              </div>
            )}

            {/* View Public Advisor Page — opens the dealer's /slug in a new
                tab. Available to any authenticated dealer. Does NOT expose
                any admin-only query params. */}
            {dealer?.slug ? (
              <button
                onClick={openMyPublicPage}
                className={`hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                  isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                }`}
                data-testid="view-public-dealer-page"
                title={`Open /${dealer.slug} in a new tab`}
              >
                <ExternalLink size={14} /> View Public Advisor Page
              </button>
            ) : null}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-xl transition-colors ${
                isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
              }`}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Google Contacts connect — dashboard-level status pill.
                Once connected here, every lead card can push contacts
                without another OAuth round-trip. */}
            <GoogleConnectHeader isDark={isDark} />

            {/* Getting Started / Help — opens the walkthrough modal */}
            <button
              onClick={() => setShowGettingStarted(true)}
              className={`p-2 rounded-xl transition-colors ${
                isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
              }`}
              data-testid="help-btn"
              aria-label="Open Getting Started guide"
              title="How to use RV Match AI"
            >
              <HelpCircle size={20} />
            </button>

            {/* Manage billing — Stripe Customer Portal. Only shown when
                the dealer has a Stripe customer record (i.e. they've
                actually paid at some point). Silent for trial dealers. */}
            {dealer?.stripe_customer_id && (
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/payments/portal`,
                      { email: dealer.email },
                    );
                    if (res.data?.url) window.location.href = res.data.url;
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Billing portal unavailable');
                  }
                }}
                className={`p-2 rounded-xl transition-colors ${
                  isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
                }`}
                data-testid="dealer-manage-billing-btn"
                aria-label="Manage billing and invoices"
                title="Manage billing"
              >
                <Receipt size={20} />
              </button>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className={`p-2 rounded-xl transition-colors ${
                isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-500'
              }`}
              data-testid="logout-btn"
            >
              <LogOut size={20} />
            </button>
          </div>
        </header>

        {/* Preview mode banner — shows for trial dealers who haven't subscribed yet.
            Explains that real lead flow + write actions require a paid plan. */}
        {dealer?.subscription_status === 'trial' && (
          <div
            className="bg-gradient-to-r from-cyan-500/15 via-cyan-500/10 to-amber-500/15 border-b border-cyan-400/25 backdrop-blur-sm px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap"
            data-testid="dealer-preview-banner"
          >
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 text-[10px] font-bold uppercase tracking-widest">
                Preview
              </span>
              <p className="text-sm text-cyan-100 leading-tight">
                You're exploring the dashboard.{' '}
                <span className="text-cyan-300">Subscribe to activate real lead flow, email sending, and CRM actions.</span>
              </p>
            </div>
            <button
              onClick={() => navigate('/dealers')}
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#F2A900] hover:bg-amber-400 text-black text-xs font-bold whitespace-nowrap transition-colors"
              data-testid="dealer-preview-upgrade-btn"
            >
              See plans →
            </button>
          </div>
        )}

        {/* Grace period banner — subscription was cancelled but the 7-day
            grace window is still open. Shows the countdown so the dealer
            can re-subscribe or update their card before losing access. */}
        {dealer?.subscription_status === 'grace_period' && dealer?.grace_period_ends_at && (() => {
          const endMs = new Date(dealer.grace_period_ends_at).getTime();
          const daysLeft = Math.max(0, Math.ceil((endMs - Date.now()) / 86400000));
          const hoursLeft = Math.max(0, Math.ceil((endMs - Date.now()) / 3600000));
          const label = daysLeft > 1 ? `${daysLeft} days left` : hoursLeft > 0 ? `${hoursLeft} hours left` : 'ending soon';
          return (
            <div
              className="bg-gradient-to-r from-amber-500/15 via-orange-500/10 to-rose-500/15 border-b border-amber-400/30 backdrop-blur-sm px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap"
              data-testid="dealer-grace-banner"
            >
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-[10px] font-bold uppercase tracking-widest">
                  Grace Period
                </span>
                <p className="text-sm text-amber-100 leading-tight">
                  Your subscription was cancelled — you have{' '}
                  <span className="text-amber-300 font-bold" data-testid="grace-banner-days-left">{label}</span>{' '}
                  to update payment before dashboard access pauses. Your leads, inventory, and settings stay safe.
                </p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/payments/portal`,
                      { email: dealer.email },
                    );
                    if (res.data?.url) window.location.href = res.data.url;
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Billing portal unavailable');
                  }
                }}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-xs font-bold whitespace-nowrap transition-colors"
                data-testid="dealer-grace-update-btn"
              >
                Update payment →
              </button>
            </div>
          );
        })()}

        {/* Past-due banner — recurring invoice failed but subscription is
            still active. Same UX as grace but different urgency copy. */}
        {dealer?.subscription_status === 'past_due' && (
          <div
            className="bg-gradient-to-r from-rose-500/15 via-red-500/10 to-orange-500/15 border-b border-rose-400/30 backdrop-blur-sm px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap"
            data-testid="dealer-past-due-banner"
          >
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/40 text-rose-200 text-[10px] font-bold uppercase tracking-widest">
                Payment Failed
              </span>
              <p className="text-sm text-rose-100 leading-tight">
                Your latest invoice couldn't be charged.{' '}
                <span className="text-rose-300">Update your payment method to keep your advisor account active.</span>
              </p>
            </div>
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/payments/portal`,
                    { email: dealer.email },
                  );
                  if (res.data?.url) window.location.href = res.data.url;
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Billing portal unavailable');
                }
              }}
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-rose-400 hover:bg-rose-300 text-black text-xs font-bold whitespace-nowrap transition-colors"
              data-testid="dealer-past-due-update-btn"
            >
              Update payment →
            </button>
          </div>
        )}

        {/* Page Content */}
        <div className="p-6">
          {currentTab === 'overview' && (
            <div className="space-y-6">
              {/* "Get Your First RV Match Lead" — 6-step setup panel with
                  working action buttons. Auto-collapses once setup is done
                  AND a real (non-test) lead has landed. */}
              <OnboardingChecklist
                dealer={dealer}
                leads={leads}
                analytics={analytics}
                isDark={isDark}
                onOpenHelp={() => setShowGettingStarted(true)}
                onGoTo={(tab) => setSearchParams({ tab })}
                onTestLeadCreated={async () => {
                  const t = getAuthToken();
                  if (!t) return;
                  const leadsRes = await axios.get(`${API}/dealer/leads`,
                    { headers: { Authorization: `Bearer ${t}` } });
                  setLeads(leadsRes.data);
                }}
              />

              {/* Link-conversion insight — appears when there are clicks
                  but zero real (non-test) completed leads. */}
              <LinkConversionInsight
                clickCount={analytics?.dealer_link_clicks || 0}
                realLeadCount={leads.filter((l) => !l.is_test).length}
                isDark={isDark}
                onOpenPublicPage={openMyPublicPage}
                onGoTo={(tab) => setSearchParams({ tab })}
                onCopyLink={copyMyQuizLink}
              />

              {/* AI Briefing */}
              <AIBriefing leads={leads} isDark={isDark} />

              {/* Quick Stats */}
              <QuickStats leads={leads} analytics={analytics} isDark={isDark} />
              
              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Analytics */}
                <div className="lg:col-span-2">
                  <DealerAnalyticsSection
                    analytics={analytics}
                    isLoading={false}
                    isDark={isDark}
                    dealer={dealer}
                    onGoTo={(tab) => setSearchParams({ tab })}
                    onCreateTestLead={createTestLead}
                    onCopyLink={copyMyQuizLink}
                    onRangeChange={(days, includeTest) => {
                      const token = getAuthToken();
                      axios.get(`${API}/dealer/analytics`, {
                        headers: { Authorization: `Bearer ${token}` },
                        params: { days, include_test: includeTest ? 'true' : 'false' }
                      }).then(res => setAnalytics(res.data));
                    }}
                  />
                </div>
                
                {/* Activity Feed */}
                <div className="lg:col-span-1">
                  <ActivityFeed 
                    leads={leads} 
                    isDark={isDark}
                    onLeadClick={handleLeadClickById}
                    onCopyLink={copyMyQuizLink}
                    onCreateTestLead={createTestLead}
                  />
                </div>
              </div>
            </div>
          )}

          {currentTab === 'pipeline' && (
            <div className="space-y-4">
              {/* Pipeline Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Lead Pipeline
                  </h2>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Drag and drop leads to update their status
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => setShowAddLead(true)}
                    className="flex items-center gap-2 bg-[#004E86] hover:bg-[#003a65] text-white"
                    data-testid="add-lead-btn"
                  >
                    <UserPlus size={14} />
                    Add Lead
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportLeads('csv')}
                    className="flex items-center gap-2"
                    data-testid="export-csv"
                  >
                    <Download size={14} />
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportLeads('json')}
                    className="flex items-center gap-2"
                    data-testid="export-json"
                  >
                    <Download size={14} />
                    JSON
                  </Button>
                </div>
              </div>
              
              {/* Kanban Board */}
              <KanbanPipeline 
                leads={leads}
                onLeadClick={handleLeadClick}
                onStatusChange={updateLeadStatus}
                onConvertToSale={handleConvertToSale}
                isDark={isDark}
              />
            </div>
          )}

          {currentTab === 'email' && (
            <EmailCenterTab isDark={isDark} leads={leads} />
          )}

          {currentTab === 'sales' && (
            <SalesTrackerTab 
              isDark={isDark} 
              leads={leads}
              prefilledLead={leadToConvert}
            />
          )}

          {currentTab === 'appointments' && (
            <AppointmentsTab 
              isDark={isDark} 
              onCountUpdate={setPendingCallCount}
            />
          )}

          {currentTab === 'signups' && dealer?.email === 'malleg85@gmail.com' && (
            <DealerSignupsTab isDark={isDark} />
          )}

          {/* Inventory stays mounted (display:none when inactive) so the
              iframe/scroll position/filters persist when the dealer switches
              tabs. Reloading it every visit was resetting the whole embed. */}
          <div style={{ display: currentTab === 'inventory' ? 'block' : 'none' }}>
            <InventoryManagerEmbed isDark={isDark} />
          </div>

          {currentTab === 'crm' && (
            <CRMHubEmbed isDark={isDark} />
          )}

          {currentTab === 'research' && (
            <WebResearchEmbed isDark={isDark} />
          )}

          {currentTab === 'resources' && (
            <ResourcesTab isDark={isDark} dealer={dealer} />
          )}

          {currentTab === 'marketing' && (
            <MarketingTab token={getAuthToken()} isDark={isDark} dealer={dealer} />
          )}

          {currentTab === 'war-board' && (
            <div className={isDark ? 'bg-[#0b0e14] min-h-screen' : ''}>
              <SalesWarBoard />
            </div>
          )}

          {currentTab === 'intake-form' && (
            <div className={isDark ? 'bg-[#0b0e14] min-h-screen' : ''}>
              <CustomerIntakeForm />
            </div>
          )}

          {currentTab === 'trades' && (
            <div className={isDark ? 'bg-[#0b0e14] min-h-screen' : ''}>
              <TradesTab isDark={isDark} dealer={dealer} />
            </div>
          )}

          {currentTab === 'support' && (
            <div className={isDark ? 'bg-[#0b0e14] min-h-screen p-4' : 'p-4'}>
              <DealerSupportChat isDark={isDark} />
            </div>
          )}

          {currentTab === 'admin' && dealer?.email === 'malleg85@gmail.com' && (
            <AdminPanel isDark={isDark} />
          )}
        </div>
      </main>

      {/* Command Bar */}
      <CommandBar 
        isOpen={showCommandBar}
        onClose={() => setShowCommandBar(false)}
        leads={leads}
        isDark={isDark}
      />

      {/* Getting Started walkthrough — always mounted so ESC/backdrop always work. */}
      <GettingStartedModal
        open={showGettingStarted}
        onClose={() => setShowGettingStarted(false)}
        isDark={isDark}
        onGoTo={(tab) => setSearchParams({ tab })}
      />

      {/* Lead Detail Modal */}
      <LeadDetailModal 
        isOpen={showLeadModal} 
        onClose={() => { setShowLeadModal(false); fetchNotifications(); }} 
        lead={selectedLead}
        onLeadUpdate={(updatedLead) => {
          setLeads(leads.map(lead => 
            lead.id === updatedLead.id ? updatedLead : lead
          ));
          setSelectedLead(updatedLead);
          fetchNotifications();
        }}
        onConvertToSale={handleConvertToSale}
        onDelete={deleteLead}
      />

      {/* AI Quiz GPT Chat Modal */}
      <Dialog open={showAIChat} onOpenChange={setShowAIChat}>
        <DialogContent className={`max-w-lg h-[600px] flex flex-col p-0 overflow-hidden ${isDark ? 'bg-slate-900 border-slate-800' : ''}`}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-bold">AI Form GPT</h3>
                <p className="text-xs text-white/70">Customize your form experience</p>
              </div>
            </div>
            <button onClick={() => setShowAIChat(false)} className="text-white/70 hover:text-white">
              <X size={20} />
            </button>
          </div>

          {/* Chat Messages */}
          <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
            {aiMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : isDark ? 'bg-slate-700 text-white rounded-bl-md' : 'bg-white text-slate-900 rounded-bl-md shadow-sm'
                }`}>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex justify-start">
                <div className={`rounded-2xl rounded-bl-md px-4 py-3 ${isDark ? 'bg-slate-700' : 'bg-white shadow-sm'}`}>
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className={`p-4 border-t shrink-0 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
            <div className="flex gap-2">
              <Input
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                placeholder="Ask about form customization..."
                className={`flex-1 ${isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && aiInput.trim() && !aiLoading) {
                    handleAIChatSubmit();
                  }
                }}
              />
              <Button
                onClick={handleAIChatSubmit}
                disabled={!aiInput.trim() || aiLoading}
                className="bg-blue-600 text-white hover:bg-blue-700"
              >
                <Send size={18} />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Add Lead */}
      <AddLeadModal
        open={showAddLead}
        onOpenChange={setShowAddLead}
        isDark={isDark}
        onCreated={async () => {
          try {
            const t = getAuthToken();
            const leadsRes = await axios.get(`${API}/dealer/leads`, { headers: { Authorization: `Bearer ${t}` } });
            setLeads(leadsRes.data);
          } catch { /* noop */ }
        }}
      />
    </div>
  );
};

export default DealerDashboard;
